import * as Accordion from '@radix-ui/react-accordion';
import type { CheckedState } from '@radix-ui/react-checkbox';
import type { ChangeEvent, Dispatch, FC, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { Checkbox } from '~/components/Checkbox';
import { cn } from '~/lib/cn';

import type { CheckboxTreeItem } from './types.ts';
import {
  applyTreeValue,
  calculateChecked,
  findCheckedItems,
  getCheckboxIndentClass,
  isAnyItemChecked,
  searchCheckboxTreeWithStructure,
} from './utils.ts';

type CheckboxTreeProps = {
  value: CheckboxTreeItem[];
  setValue: Dispatch<SetStateAction<CheckboxTreeItem[]>>;
  onChange?(value: CheckboxTreeItem[]): void;
  showSearchBar?: boolean;
  showSelectAllButton?: boolean;
  showCaption?: boolean;
  itemTypePlural?: string;
  depth?: number;
};

export const CheckboxTree: FC<CheckboxTreeProps> = ({
  value,
  setValue,
  onChange,
  showSelectAllButton = true,
  showCaption = false,
  itemTypePlural = '',
  depth = 0,
}) => {
  const { t } = useTranslation('roster');
  const isRoot = depth === 0;

  const headerClassNames = 'flex flex-row items-center';
  const triggerClassNames =
    'font-semibold flex flex-1 h-2xl px-lg items-center justify-end text-body-sm [&>i]:data-[state=open]:rotate-180';

  const onCheckedChange = useCallback(
    (
      state: CheckedState,
      treeItem: CheckboxTreeItem | CheckboxTreeItem[],
      index: number = 0,
    ) => {
      const newValue = [...value];

      if (Array.isArray(treeItem)) {
        treeItem.forEach((item) => {
          newValue[value.indexOf(item)] = applyTreeValue(Boolean(state), item);
        });
      } else {
        newValue[index] = applyTreeValue(Boolean(state), treeItem);
      }
      onChange?.(newValue);
    },
    [value, onChange],
  );

  const onExpansionChange = useCallback(
    (state: boolean, index: number) => {
      const newValue = [...value];
      newValue[index].isExpanded = state;
      onChange?.(newValue);
    },
    [value, onChange],
  );

  const onChangeInTree = useCallback(
    (treeItem: CheckboxTreeItem[], index: number) => {
      const newValue = [...value];
      newValue[index].children = treeItem;
      onChange?.(newValue);
    },
    [value, onChange],
  );

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(searchCheckboxTreeWithStructure(value, event.target.value));
  };

  const checkedItems = useMemo(() => findCheckedItems(value), [value]);

  return (
    <div className="flex flex-col items-center bg-level-03">
      {isRoot ? (
        <div className="flex w-full flex-col">
          <div className="relative flex items-center justify-between p-md">
            <Icon
              className="fa-light fa-search fa-sm pointer-events-none absolute pl-sm text-subtle"
              type="magnifying-glass"
              variant="solid"
              size="sm"
              aria-hidden
            />
            <input
              type="input"
              className={cn(
                'grow rounded-sm py-sm pl-xl text-body-sm duration-300 ease-out text-default placeholder:text-subtle',
                'outline outline-1 bg-emphasis-default outline-emphasis-subtle hover:bg-emphasis-strong hover:outline-emphasis-stronger focus:placeholder:text-default',
              )}
              placeholder={t('checkboxTree.labels.searchPlaceholder')}
              onChange={handleSearchInputChange}
              aria-label={t('checkboxTree.labels.searchInputLabel')}
            />
          </div>
          {showSelectAllButton ? (
            <div className="px-md pb-md">
              {isAnyItemChecked(value) ? (
                <Button
                  variant="secondary"
                  className="w-full border font-semibold leading-6"
                  onClick={() => {
                    onCheckedChange(
                      false,
                      value.filter((item) => item.isVisible),
                    );
                  }}
                >
                  {t('checkboxTree.labels.clearAll')}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  className="w-full border font-semibold leading-6"
                  onClick={() => {
                    onCheckedChange(
                      true,
                      value.filter((item) => item.isVisible),
                    );
                  }}
                >
                  {t('checkboxTree.labels.selectAll')}
                </Button>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
      {showCaption ? (
        <div className="w-full px-md pb-sm text-start text-caption-md text-subtle">
          {checkedItems.length
            ? checkedItems.length > 1
              ? t('checkboxTree.labels.caption', {
                  count: checkedItems.length,
                  type: itemTypePlural ?? '',
                })
              : checkedItems[0].label
            : null}
        </div>
      ) : null}
      <Accordion.Root
        type="multiple"
        className="w-full"
        value={value
          .filter((item) => item.isExpanded)
          .map((item) => item.label)}
      >
        {isRoot && !value.some((item) => item.isVisible) ? (
          <div className="p-md pb-xl text-center text-body-sm text-subtle">
            {t('checkboxTree.labels.noResults')}
          </div>
        ) : null}
        {value
          .filter((item) => item.isVisible)
          .map((item) => (
            <Accordion.Item
              value={item.label}
              key={`${item.id}:${item.label}`}
              className={cn(
                'bg-level-02 hover:bg-level-01 data-[state=closed]:hover:bg-level-01',
                isRoot ? 'data-[state=closed]:bg-level-03' : '',
              )}
            >
              <Accordion.Header className={headerClassNames}>
                <Checkbox
                  className={cn(
                    'w-full py-md text-body-sm tracking-wide',
                    getCheckboxIndentClass(depth),
                  )}
                  label={item.label}
                  checked={calculateChecked(item)}
                  onCheckedChange={(state) =>
                    onCheckedChange(state, item, value.indexOf(item))
                  }
                />
                {item.children?.length ? (
                  <Accordion.Trigger
                    className={triggerClassNames}
                    onClick={() => {
                      onExpansionChange(!item.isExpanded, value.indexOf(item));
                    }}
                    disabled={!item.children?.length}
                  >
                    <Icon
                      type="chevron-down"
                      variant="solid"
                      className="text-white"
                      size="md"
                      aria-hidden
                      aria-label="dropdown-chevron"
                    />
                  </Accordion.Trigger>
                ) : null}
              </Accordion.Header>
              {item.children?.length ? (
                <Accordion.Content>
                  <CheckboxTree
                    value={item.children}
                    setValue={setValue}
                    depth={depth + 1}
                    onChange={(newValue) =>
                      onChangeInTree(newValue, value.indexOf(item))
                    }
                  />
                </Accordion.Content>
              ) : null}
            </Accordion.Item>
          ))}
      </Accordion.Root>
    </div>
  );
};

CheckboxTree.displayName = 'CheckboxTree';
